// Import Dependencies
import PropTypes from "prop-types";
import { useMemo } from "react";

// Local Imports
import {
  Pagination,
  PaginationItems,
  PaginationNext,
  PaginationPrevious,
} from "components/ui";
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import clsx from "clsx";
import { useBreakpointsContext } from "app/contexts/breakpoint/context";
import { useMediaQuery } from "hooks/useMediaQuery";

// ----------------------------------------------------------------------

export function PaginationSection({ table, compact = false }) {
  const paginationState = table.getState().pagination;
  const { isXl, is2xl } = useBreakpointsContext();
  const isMobile = useMediaQuery("(max-width: 639px)");

  const pageSizeOptions = useMemo(() => {
    const defaultOptions = [100, 200, 300, 400, 500, 1000];
    const currentSize = paginationState.pageSize;
    if (currentSize && !defaultOptions.includes(currentSize)) {
      return [...defaultOptions, currentSize].sort((a, b) => a - b);
    }
    return defaultOptions;
  }, [paginationState.pageSize]);

  const visibleRowsCount = table.getRowModel().rows.filter(row => !row.original?.isSeparator).length;
  const totalCoreRowsCount = table.getCoreRowModel().rows.filter(row => !row.original?.isSeparator).length;

  const entriesText = visibleRowsCount === 0 ? (
    "0 entries"
  ) : (
    <>
      {paginationState.pageIndex * paginationState.pageSize + 1} -{" "}
      {paginationState.pageIndex * paginationState.pageSize + visibleRowsCount} of{" "}
      {table.options.meta?.totalCount !== undefined ? table.options.meta.totalCount : totalCoreRowsCount} entries
    </>
  );

  if (compact === "tiny") {
    return (
      <div className="flex flex-col items-center justify-center gap-2 w-full py-2">
        <Pagination
          total={table.getPageCount()}
          value={paginationState.pageIndex + 1}
          onChange={(page) => table.setPageIndex(page - 1)}
          siblings={0}
          boundaries={0}
        >
          <PaginationPrevious />
          <PaginationNext />
        </Pagination>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-y-2 gap-x-4 w-full py-1">
      {/* Left: Show Entries */}
      <div className={`flex items-center gap-2 text-[11px] text-slate-600 dark:text-dark-200 shrink-0 ${compact ? "order-1" : ""}`}>
        <span>Show</span>
        <div className="relative w-fit">
          <Listbox
            value={paginationState.pageSize}
            onChange={(val) => {
              table.setPageSize(Number(val));
            }}
          >
            <ListboxButton className="flex items-center justify-between gap-1 w-full h-7 rounded-full bg-white dark:bg-dark-800 py-1 pl-3 pr-2 text-left text-[11px] text-gray-700 dark:text-dark-100 border border-gray-300 dark:border-dark-500 hover:border-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500 min-w-[64px] transition-colors">
              <span className="block truncate leading-none">{paginationState.pageSize}</span>
              <ChevronDownIcon className="h-4 w-4 text-gray-500 dark:text-gray-400 shrink-0" aria-hidden="true" />
            </ListboxButton>
            <ListboxOptions 
              anchor="top"
              transition
              className="z-[9999] min-w-[5rem] w-max rounded-lg border border-gray-300 bg-white py-1 shadow-lg shadow-gray-200/50 outline-hidden dark:border-dark-500 dark:bg-dark-750 dark:shadow-none transition duration-100 ease-out data-[closed]:scale-95 data-[closed]:opacity-0"
            >
              {pageSizeOptions.map((size, sizeIdx) => (
                <ListboxOption
                  key={sizeIdx}
                  value={size}
                  className={({ focus, selected }) =>
                    clsx(
                      "flex h-8 w-full items-center px-3 text-[11px] outline-hidden transition-colors cursor-pointer whitespace-nowrap",
                      focus || selected ? "bg-gray-100 dark:bg-dark-600 text-gray-900 dark:text-white" : "text-gray-700 dark:text-dark-200",
                      selected && "font-semibold"
                    )
                  }
                >
                  {size}
                </ListboxOption>
              ))}
            </ListboxOptions>
          </Listbox>
        </div>
        <span className="hidden min-[360px]:inline">entries</span>
      </div>

      {/* Center: Pagination Buttons */}
      <div className={`flex shrink-0 overflow-x-auto hide-scrollbar ${compact ? "order-3 justify-center w-full pt-2 border-t border-slate-100 dark:border-dark-700 mt-1" : "justify-center"}`}>
        <Pagination
          total={table.getPageCount()}
          value={paginationState.pageIndex + 1}
          onChange={(page) => table.setPageIndex(page - 1)}
          siblings={isXl ? 2 : is2xl ? 3 : isMobile ? 0 : 1}
          boundaries={isXl ? 2 : isMobile ? 0 : 1}
        >
          <PaginationPrevious />
          <PaginationItems />
          <PaginationNext />
        </Pagination>
      </div>

      {/* Right: Info text */}
      <div className={`text-[11px] text-slate-600 dark:text-dark-200 shrink-0 ${compact ? "order-2" : "text-right"}`}>
        {entriesText}
      </div>
    </div>
  );
}

PaginationSection.propTypes = {
  table: PropTypes.object,
};
